import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardT } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import { ACTIVE_DASHBOARDS_QUERY_KEY } from 'shared/api/getDashboardsList';
import { snackbarService } from 'shared/lib/snackbar/service';
import { RQAPICallWithData } from 'store/APIClient';

const createDashboard = async (title: string, folderId: number | null) => {
    const response = await RQAPICallWithData<DashboardT>({
        endpoint: `explore/dashboards`,
        body: { title, folderId },
        method: 'POST',
    });
    return response.content;
};

/**
 * RQ Mutation to create a new dashboard.
 * The mutation takes as a parameter the title of the new dashboard.
 * @param {(dashboard: DashboardT) => void} onSuccess - A callback function to be executed on success
 */
export const useCreateDashboard = (onSuccess?: (arg1: DashboardT) => void) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            title,
            folderId,
        }: {
            title: string;
            folderId: number | null;
        }) => createDashboard(title, folderId),

        onSuccess: ({ data: createdDashboard, message }) => {
            trackEvent('decision/new-dashboard-created');
            queryClient.setQueryData(
                [ACTIVE_DASHBOARDS_QUERY_KEY],
                (oldDashboards: DashboardT[]) => [
                    ...oldDashboards,
                    createdDashboard,
                ],
            );
            snackbarService.showInfo(message);
            if (onSuccess) onSuccess(createdDashboard);
        },
    });
};
